import React, {useState, useEffect} from 'react';
import api from '../services/api';
import '../styles/ProjectDetails.css';
import {formatDate} from "../utils/dateConverter";
import CustomReportModal from '../modals/CustomReportModal';

export default function ProjectDetails({project}) {
    const getCurrentMonthRange = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 2);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        const formatDateStr = (date) => date.toISOString().split('T')[0];
        return {
            startDate: formatDateStr(firstDay),
            endDate: formatDateStr(lastDay)
        };
    };

    const [dateRange, setDateRange] = useState(getCurrentMonthRange());
    const [projectStats, setProjectStats] = useState(null);
    const [editModeUserId, setEditModeUserId] = useState(null);
    const [editedEntries, setEditedEntries] = useState([]);
    const [editProjectReportModalOpen, setEditProjectReportModalOpen] = useState(false);

    const loadProjectStats = () => {
        if (!dateRange.startDate || !dateRange.endDate) {
            alert("Please select start and end date.");
            return;
        }

        api.post(`/project/projectStats/${project.id}`, dateRange)
            .then(res => setProjectStats(res.data))
            .catch(() => alert("Failed to load stats"));
    };

    const generateReport = () => {
        api.post(`/project/Report/${project.id}`, dateRange, {
            responseType: 'blob'
        }).then(res => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `ProjectReport_${project.id}.zip`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }).catch(err => {
            alert("Failed to generate report");
        });
    };

    const handleEditToggle = (userId, entries) => {
        if (editModeUserId === userId) {
            setEditModeUserId(null);
            setEditedEntries([]);
        } else {
            setEditModeUserId(userId);
            setEditedEntries(entries);
        }
    };

    const handleEntryChange = (index, field, value) => {
        const updated = [...editedEntries];
        updated[index] = {...updated[index], [field]: value};
        setEditedEntries(updated);
    };

    const handleSaveChanges = (userId) => {
        if (!project?.id) {
            alert("Projekt-ID fehlt.");
            return;
        }

        // Transform entries to match backend expectations
        const payloadEntries = editedEntries.map(entry => ({
            id: entry.id,
            projectId: project.id,
            date: entry.date,
            startTime: entry.startTime,
            endTime: entry.endTime,
            breakMinutes: entry.breakMinutes ?? 0,
            travelMinutes: entry.travelMinutes ?? 0,
            comment: entry.comment ?? '',
            activity: entry.activity ?? ''
        }));

        console.log("Sending payload:", {userId, entries: payloadEntries});

        api.patch(`/timeEntries/bulkEdit`, {
            userId,
            entries: payloadEntries
        }).then(() => {
            setEditModeUserId(null);
            loadProjectStats();
        }).catch(() => alert("Speichern fehlgeschlagen"));
    };

    useEffect(() => {
        if (project?.id) {
            setProjectStats(null);
            loadProjectStats();
        }
    }, [project, dateRange]);


    const handleSubmitCustomReport = async (bulkData) => {
        if (!project?.id) {
            alert('Projekt-ID fehlt.');
            return;
        }

        try {
            const requestBody = {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                users: bulkData.map(block => ({
                    userId: block.userId,
                    entries: block.entries.map(e => ({
                        userId: block.userId,
                        projectId: project.id,
                        date: e.date,                    // "yyyy-MM-dd"
                        startTime: e.startTime,          // "HH:mm"
                        endTime: e.endTime,              // "HH:mm"
                        breakMinutes: Number(e.breakMinutes) || 0,
                        travelMinutes: Number(e.travelMinutes) || 0,
                        kilometers: e.kilometers ?? 0,
                        location: e.location ?? null,
                        comment: e.comment ?? null,
                        activity: e.activity ?? null
                    }))
                }))
            };

            const res = await api.post(`/CustomReport/bulk/${project.id}`, requestBody, {
                responseType: 'blob'
            });

            // Download the zip
            const blob = new Blob([res.data], {type: 'application/zip'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `ProjectReports_${project.id}.zip`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (err) {
            console.error(err);
            alert('Fehler beim Speichern/Erstellen des Berichts.');
        }
    };

    return (
        <div className="project-details-container">

            <div className="date-range">
                <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={e => setDateRange({...dateRange, startDate: e.target.value})}
                />
                <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={e => setDateRange({...dateRange, endDate: e.target.value})}
                />
                <button onClick={loadProjectStats} className="button">Load Stats</button>


                <button onClick={generateReport} className="button">Report</button>
                <button onClick={setEditProjectReportModalOpen} className="button">Custom Report</button>
            </div>

            {projectStats !== null && (
                <div className="project-stats">
                    {projectStats.length > 0 ? (
                        projectStats.map(user => (
                            <div key={user.userId} className="user-stat">
                                <h4>{user.username} — Total: {user.totalWorkTime}</h4>
                                <div style={{marginBottom: '10px'}}>
                                    {editModeUserId !== user.userId ? (
                                        <button className="button"
                                                onClick={() => handleEditToggle(user.userId, user.entries)}>Bearbeiten</button>
                                    ) : (
                                        <>
                                            <button className="button"
                                                    onClick={() => handleSaveChanges(user.userId)}>Speichern
                                            </button>
                                            <button className="button button-danger"
                                                    onClick={() => handleEditToggle(user.userId, user.entries)}>Abbrechen
                                            </button>
                                        </>
                                    )}
                                </div>
                                <table className="table">
                                    <thead>
                                    <tr>
                                        <th style={{display: 'none'}}>Id</th>
                                        <th>Datum</th>
                                        <th>Wochentag</th>
                                        <th>Start</th>
                                        <th>Ende</th>
                                        <th>Arbeitszeit</th>
                                        <th>Pause [min]</th>
                                        <th>Fahrtzeiten [min]</th>
                                        <th>Aktivität</th>
                                        <th>Comment</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(editModeUserId === user.userId ? editedEntries : user.entries).map((entry, index) => (
                                        <tr key={index}>
                                            <td style={{display: 'none'}}>{entry.id}</td>
                                            <td>{formatDate(entry.date)}</td>
                                            <td>{entry.dayOfWeek}</td>
                                            <td>
                                                {editModeUserId === user.userId ? (
                                                    <input
                                                        type="time"
                                                        value={entry.startTime}
                                                        onChange={e => handleEntryChange(index, 'startTime', e.target.value)}
                                                    />
                                                ) : entry.startTime}
                                            </td>
                                            <td>
                                                {editModeUserId === user.userId ? (
                                                    <input
                                                        type="time"
                                                        value={entry.endTime}
                                                        onChange={e => handleEntryChange(index, 'endTime', e.target.value)}
                                                    />
                                                ) : entry.endTime}
                                            </td>
                                            <td>{entry.workTime}</td>
                                            <td>
                                                {editModeUserId === user.userId ? (
                                                    <input
                                                        type="number"
                                                        value={entry.breakMinutes}
                                                        onChange={e => handleEntryChange(index, 'breakMinutes', e.target.value)}
                                                        style={{width: '60px'}}
                                                    />
                                                ) : `${entry.breakMinutes} min`}
                                            </td>
                                            <td>
                                                {editModeUserId === user.userId ? (
                                                    <input
                                                        type="number"
                                                        value={entry.travelMinutes}
                                                        onChange={e => handleEntryChange(index, 'travelMinutes', e.target.value)}
                                                        style={{width: '60px'}}
                                                    />
                                                ) : `${entry.travelMinutes} min`}
                                            </td>
                                            <td>
                                                {editModeUserId === user.userId ? (
                                                    <input
                                                        type="text"
                                                        value={entry.activity}
                                                        onChange={e => handleEntryChange(index, 'activity', e.target.value)}
                                                    />
                                                ) : entry.activity}
                                            </td>
                                            <td>
                                                {editModeUserId === user.userId ? (
                                                    <input
                                                        type="text"
                                                        value={entry.comment}
                                                        onChange={e => handleEntryChange(index, 'comment', e.target.value)}
                                                    />
                                                ) : entry.comment}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ))
                    ) : (
                        <p>Keine Einträge in diesem Bereich gefunden.</p>
                    )}

                </div>
            )}
            {editProjectReportModalOpen && (
                <CustomReportModal
                    users={projectStats || []}
                    dateRange={dateRange}
                    onClose={() => setEditProjectReportModalOpen(false)}
                    onSubmit={handleSubmitCustomReport}
                />
            )}
        </div>
    );
}
